/**
 * PaymentReceipts — staff-only collapsible panel for uploading payment
 * receipts to a matter, running Claude vision extraction, and overriding
 * extracted values manually.
 *
 * Schema lives in supabase/migrations/20260508000001_payment_receipts.sql.
 * Extraction is performed by the `extract-payment-receipt` edge function
 * (claude-haiku-4-5) with hard cost caps:
 *   - $0.05 per receipt (refuse re-runs after that)
 *   - $5/day across the whole project
 *
 * Clients have NO RLS access to payment_receipts — this component is
 * gated by `isStaff` at the call site (MatterOverview) too, but every
 * fetch here would already return zero rows for non-staff.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Receipt,
  Upload,
  Loader2,
  ChevronDown,
  ChevronUp,
  X,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Clock,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useMatter } from '../hooks/useMatter';

const STORAGE_BUCKET = 'payment-receipts';
const DAILY_CAP_USD = 5.0;

const ALLOWED_MIMES = new Set([
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
  'image/webp',
  'application/pdf',
]);
const MAX_BYTES = 20 * 1024 * 1024; // 20 MB — matches bucket cap

type ReceiptStatus = 'pending' | 'extracted' | 'verified' | 'failed';

interface PaymentReceiptRow {
  id: string;
  matter_id: string;
  uploaded_by_contact_id: string | null;
  file_path: string;
  file_name: string;
  content_type: string | null;
  size_bytes: number | null;
  ai_extracted_at: string | null;
  ai_extracted_date: string | null;
  ai_extracted_amount: number | null;
  ai_extracted_currency: string | null;
  ai_extracted_method: string | null;
  ai_cost_usd: number | null;
  manual_override: Record<string, unknown> | null;
  status: ReceiptStatus;
  created_at: string;
  uploaded_by:
    | { given_names: string | null; family_name: string | null }
    | null;
}

interface PaymentReceiptsProps {
  matterId: string;
}

export default function PaymentReceipts({ matterId }: PaymentReceiptsProps) {
  const { contact: viewerContact } = useMatter();
  const [open, setOpen] = useState(false); // default collapsed
  const [rows, setRows] = useState<PaymentReceiptRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [extractingId, setExtractingId] = useState<string | null>(null);
  const [openRow, setOpenRow] = useState<PaymentReceiptRow | null>(null);
  const [dailyTotalUsd, setDailyTotalUsd] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        // deno-lint-ignore-style cast — table types not in database.types yet
        .from('payment_receipts' as never)
        .select(
          'id, matter_id, uploaded_by_contact_id, file_path, file_name, content_type, size_bytes, ai_extracted_at, ai_extracted_date, ai_extracted_amount, ai_extracted_currency, ai_extracted_method, ai_cost_usd, manual_override, status, created_at, uploaded_by:contacts!payment_receipts_uploaded_by_contact_id_fkey(given_names, family_name)',
        )
        .eq('matter_id', matterId)
        .order('created_at', { ascending: false });
      setRows((data ?? []) as unknown as PaymentReceiptRow[]);
    } finally {
      setLoading(false);
    }
  }, [matterId]);

  const loadDailyTotal = useCallback(async () => {
    const todayStart = new Date(
      new Date().toISOString().slice(0, 10) + 'T00:00:00Z',
    ).toISOString();
    const { data } = await supabase
      .from('payment_receipts' as never)
      .select('ai_cost_usd')
      .gte('ai_extracted_at', todayStart)
      .not('ai_cost_usd', 'is', null);
    const total = ((data ?? []) as Array<{ ai_cost_usd: number | null }>).reduce(
      (sum, r) => sum + (r.ai_cost_usd ?? 0),
      0,
    );
    setDailyTotalUsd(total);
  }, []);

  useEffect(() => {
    if (!open) return;
    loadRows();
    loadDailyTotal();
  }, [open, loadRows, loadDailyTotal]);

  // ---- Upload ----
  const handleFiles = async (files: FileList | File[]) => {
    setUploadError(null);
    const list = Array.from(files);
    if (list.length === 0) return;
    setUploading(true);
    try {
      for (const file of list) {
        if (!ALLOWED_MIMES.has(file.type)) {
          throw new Error(
            `${file.name}: unsupported type (${file.type || 'unknown'}). Use JPG, PNG, HEIC, WebP, or PDF.`,
          );
        }
        if (file.size > MAX_BYTES) {
          throw new Error(`${file.name}: file is over 20 MB.`);
        }
        await uploadOneAndExtract(file);
      }
      await loadRows();
      await loadDailyTotal();
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : 'Upload failed.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const uploadOneAndExtract = async (file: File) => {
    const receiptId = crypto.randomUUID();
    const safeName = file.name.replace(/[^\w.\-+ ]/g, '_').slice(0, 200);
    const storagePath = `${matterId}/${receiptId}/${safeName}`;

    const { error: upErr } = await supabase.storage
      .from(STORAGE_BUCKET)
      .upload(storagePath, file, {
        contentType: file.type,
        upsert: false,
      });
    if (upErr) throw new Error(`${file.name}: ${upErr.message}`);

    const { error: insErr } = await supabase
      .from('payment_receipts' as never)
      // deno-lint-ignore no-explicit-any
      .insert({
        id: receiptId,
        matter_id: matterId,
        uploaded_by_contact_id: viewerContact?.id ?? null,
        file_path: storagePath,
        file_name: safeName,
        content_type: file.type || null,
        size_bytes: file.size,
        status: 'pending',
      } as never);
    if (insErr) {
      // Best-effort: try to clean up the storage object on a failed insert.
      await supabase.storage.from(STORAGE_BUCKET).remove([storagePath]);
      throw new Error(`${file.name}: ${insErr.message}`);
    }

    // Auto-trigger extraction (async — fire-and-forget; the row's status
    // refresh will pick up the result on the next load).
    triggerExtract(receiptId).catch((e) => {
      console.warn('extract failed', e);
    });
  };

  const triggerExtract = async (receiptId: string) => {
    setExtractingId(receiptId);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const accessToken = sess.session?.access_token;
      if (!accessToken) throw new Error('Not signed in');
      const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL as string) ?? '';
      if (!supabaseUrl) throw new Error('Missing VITE_SUPABASE_URL');

      const res = await fetch(`${supabaseUrl}/functions/v1/extract-payment-receipt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ receiptId }),
      });
      if (!res.ok) {
        const txt = await res.text();
        console.warn('extract endpoint returned', res.status, txt);
      }
    } finally {
      setExtractingId(null);
      await loadRows();
      await loadDailyTotal();
    }
  };

  const remainingDailyUsd = Math.max(0, DAILY_CAP_USD - dailyTotalUsd);

  // ---- Render ----
  return (
    <div className="card overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 hover:bg-slate-50 text-left"
      >
        <div className="flex items-center gap-2">
          <Receipt size={16} className="text-slate-500" />
          <h2 className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
            Payment receipts
          </h2>
          {rows.length > 0 && (
            <span className="text-xs text-slate-400">({rows.length})</span>
          )}
        </div>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {open && (
        <div className="border-t border-slate-100 p-4 space-y-3">
          {/* Upload control */}
          <div className="space-y-2">
            <label className="block">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/jpeg,image/png,image/heic,image/heif,image/webp,application/pdf"
                onChange={(e) => e.target.files && handleFiles(e.target.files)}
                disabled={uploading}
                className="hidden"
                id={`receipt-upload-${matterId}`}
              />
              <span
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold cursor-pointer
                  ${
                    uploading
                      ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                      : 'bg-[#0B2341] text-white hover:bg-[#0B2341]/90'
                  }`}
                onClick={() => !uploading && fileInputRef.current?.click()}
                role="button"
              >
                {uploading ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Upload size={14} />
                )}
                Upload receipt(s)
              </span>
              <span className="ml-2 text-xs text-slate-500">
                JPG, PNG, HEIC, WebP, or PDF. Max 20 MB each.
              </span>
            </label>
            {uploadError && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 flex items-start gap-2">
                <AlertCircle size={14} className="shrink-0 mt-0.5" />
                <span>{uploadError}</span>
              </div>
            )}
            <div className="text-xs text-slate-500">
              Daily AI cost:{' '}
              <span className="font-semibold text-[#0B2341]">
                ${dailyTotalUsd.toFixed(2)}
              </span>{' '}
              of ${DAILY_CAP_USD.toFixed(2)}/day used (
              <span className="text-slate-600">
                ${remainingDailyUsd.toFixed(2)} remaining
              </span>
              )
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="text-sm text-slate-500">Loading receipts...</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-slate-500 italic">
              No receipts uploaded yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                    <th className="pb-2 pr-2 font-semibold">File</th>
                    <th className="pb-2 pr-2 font-semibold">Uploaded by</th>
                    <th className="pb-2 pr-2 font-semibold">Date</th>
                    <th className="pb-2 pr-2 font-semibold">Amount</th>
                    <th className="pb-2 pr-2 font-semibold">Method</th>
                    <th className="pb-2 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <ReceiptRow
                      key={r.id}
                      row={r}
                      onClick={() => setOpenRow(r)}
                      isExtracting={extractingId === r.id}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {openRow && (
        <ReceiptDetailModal
          receipt={openRow}
          onClose={() => setOpenRow(null)}
          onSaved={async () => {
            await loadRows();
            await loadDailyTotal();
          }}
          onReExtract={() => triggerExtract(openRow.id)}
          isExtracting={extractingId === openRow.id}
        />
      )}
    </div>
  );
}

function ReceiptRow({
  row,
  onClick,
  isExtracting,
}: {
  row: PaymentReceiptRow;
  onClick: () => void;
  isExtracting: boolean;
}) {
  const override = row.manual_override ?? {};
  const date = (override.date as string | undefined) ?? row.ai_extracted_date;
  const amount =
    (override.amount as number | undefined) ?? row.ai_extracted_amount;
  const currency =
    (override.currency as string | undefined) ?? row.ai_extracted_currency;
  const method =
    (override.method as string | undefined) ?? row.ai_extracted_method;
  const uploadedBy = [
    row.uploaded_by?.given_names,
    row.uploaded_by?.family_name,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();
  return (
    <tr
      className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
      onClick={onClick}
    >
      <td className="py-2 pr-2 text-[#0B2341] font-medium truncate max-w-[200px]">
        {row.file_name}
      </td>
      <td className="py-2 pr-2 text-slate-600">{uploadedBy || '-'}</td>
      <td className="py-2 pr-2 text-slate-700">{date ?? '-'}</td>
      <td className="py-2 pr-2 text-slate-700">
        {amount != null
          ? `${currency ?? 'AUD'} ${Number(amount).toFixed(2)}`
          : '-'}
      </td>
      <td className="py-2 pr-2 text-slate-600">{method ?? '-'}</td>
      <td className="py-2">
        <StatusPill status={row.status} loading={isExtracting} />
      </td>
    </tr>
  );
}

function StatusPill({
  status,
  loading,
}: {
  status: ReceiptStatus;
  loading: boolean;
}) {
  if (loading) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold text-blue-700 bg-blue-50">
        <Loader2 size={11} className="animate-spin" /> Extracting
      </span>
    );
  }
  if (status === 'pending') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold text-slate-600 bg-slate-100">
        <Clock size={11} /> Pending
      </span>
    );
  }
  if (status === 'extracted') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold text-amber-700 bg-amber-50">
        <Receipt size={11} /> Extracted
      </span>
    );
  }
  if (status === 'verified') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold text-green-700 bg-green-50">
        <CheckCircle2 size={11} /> Verified
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold text-red-700 bg-red-50">
      <AlertCircle size={11} /> Failed
    </span>
  );
}

// ---------------------------------------------------------------------------
// Detail modal (image preview + editable extracted fields + re-run button)
// ---------------------------------------------------------------------------

function ReceiptDetailModal({
  receipt,
  onClose,
  onSaved,
  onReExtract,
  isExtracting,
}: {
  receipt: PaymentReceiptRow;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
  onReExtract: () => void | Promise<void>;
  isExtracting: boolean;
}) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Editable form fields — prefer manual_override over AI-extracted values.
  const override = receipt.manual_override ?? {};
  const [date, setDate] = useState<string>(
    (override.date as string | undefined) ??
      receipt.ai_extracted_date ??
      '',
  );
  const [amount, setAmount] = useState<string>(
    (override.amount as number | undefined)?.toString() ??
      receipt.ai_extracted_amount?.toString() ??
      '',
  );
  const [currency, setCurrency] = useState<string>(
    (override.currency as string | undefined) ??
      receipt.ai_extracted_currency ??
      'AUD',
  );
  const [method, setMethod] = useState<string>(
    (override.method as string | undefined) ??
      receipt.ai_extracted_method ??
      '',
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [statusToVerified, setStatusToVerified] = useState(
    receipt.status === 'verified',
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.storage
        .from(STORAGE_BUCKET)
        .createSignedUrl(receipt.file_path, 600);
      if (cancelled) return;
      if (error || !data?.signedUrl) {
        setPreviewError(error?.message ?? 'Could not load preview.');
      } else {
        setSignedUrl(data.signedUrl);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [receipt.file_path]);

  // ESC closes
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const isPdf = (receipt.content_type ?? '') === 'application/pdf';

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      // Build the manual_override payload — only include fields that
      // differ from the AI extraction so the table makes the deltas obvious.
      const overridePayload: Record<string, unknown> = {};
      const amtNum = amount.trim() === '' ? null : Number(amount);
      if (date && date !== receipt.ai_extracted_date) overridePayload.date = date;
      if (amtNum != null && amtNum !== receipt.ai_extracted_amount)
        overridePayload.amount = amtNum;
      if (currency && currency !== receipt.ai_extracted_currency)
        overridePayload.currency = currency;
      if (method && method !== receipt.ai_extracted_method)
        overridePayload.method = method;

      const update: Record<string, unknown> = {
        manual_override:
          Object.keys(overridePayload).length > 0 ? overridePayload : null,
      };
      if (statusToVerified) update.status = 'verified';

      const { error } = await supabase
        .from('payment_receipts' as never)
        // deno-lint-ignore no-explicit-any
        .update(update as never)
        .eq('id', receipt.id);
      if (error) throw error;
      await onSaved();
      onClose();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-2 sm:p-4 bg-black/70"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[94vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200">
          <div className="min-w-0 flex-1">
            <div className="text-base font-semibold text-[#0B2341] truncate">
              {receipt.file_name}
            </div>
            <div className="text-[11px] text-slate-400">
              {receipt.content_type ?? 'unknown'}
              {receipt.ai_cost_usd != null && (
                <>
                  {' '}
                  · AI cost ${Number(receipt.ai_cost_usd).toFixed(4)}
                </>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-md hover:bg-slate-100"
            title="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body — preview left, form right */}
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col xl:flex-row">
          {/* Preview */}
          <div className="bg-slate-50 grid place-items-center overflow-auto min-h-[40vh] xl:flex-1 xl:min-h-0 p-2">
            {previewError && (
              <div className="text-sm text-red-600 inline-flex items-center gap-2 px-4 text-center">
                <AlertCircle size={14} /> {previewError}
              </div>
            )}
            {!previewError && signedUrl && isPdf && (
              <iframe
                src={signedUrl}
                title={receipt.file_name}
                className="w-full h-full min-h-[60vh] border-0 bg-white"
              />
            )}
            {!previewError && signedUrl && !isPdf && (
              <img
                src={signedUrl}
                alt={receipt.file_name}
                className="max-w-full max-h-[80vh]"
              />
            )}
            {!previewError && !signedUrl && (
              <div className="text-sm text-slate-500 inline-flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Loading preview…
              </div>
            )}
          </div>

          {/* Form */}
          <div className="border-t xl:border-t-0 xl:border-l border-slate-200 bg-white xl:w-[380px] flex flex-col overflow-hidden shrink-0">
            <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  Date
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full text-sm rounded-md border border-slate-300 bg-white px-2 py-2"
                />
                {receipt.ai_extracted_date && (
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    AI extracted: {receipt.ai_extracted_date}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-[1fr_80px] gap-2">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">
                    Amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="w-full text-sm rounded-md border border-slate-300 bg-white px-2 py-2"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">
                    Currency
                  </label>
                  <input
                    type="text"
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                    maxLength={3}
                    className="w-full text-sm rounded-md border border-slate-300 bg-white px-2 py-2 uppercase"
                  />
                </div>
              </div>
              {receipt.ai_extracted_amount != null && (
                <div className="text-[10px] text-slate-400 -mt-2">
                  AI extracted: {receipt.ai_extracted_currency ?? 'AUD'}{' '}
                  {Number(receipt.ai_extracted_amount).toFixed(2)}
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  Payment method
                </label>
                <select
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                  className="w-full text-sm rounded-md border border-slate-300 bg-white px-2 py-2"
                >
                  <option value="">(unknown)</option>
                  <option value="card">Card</option>
                  <option value="bank_transfer">Bank transfer</option>
                  <option value="cash">Cash</option>
                  <option value="cheque">Cheque</option>
                  <option value="other">Other</option>
                </select>
                {receipt.ai_extracted_method && (
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    AI extracted: {receipt.ai_extracted_method}
                  </div>
                )}
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-700 pt-1">
                <input
                  type="checkbox"
                  checked={statusToVerified}
                  onChange={(e) => setStatusToVerified(e.target.checked)}
                />
                Mark as verified
              </label>

              {saveError && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                  {saveError}
                </div>
              )}
            </div>

            <div className="border-t border-slate-200 p-3 bg-slate-50 space-y-2 shrink-0">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-[#0B2341] text-white text-sm font-semibold hover:bg-[#0B2341]/90 disabled:opacity-40"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : null}
                Save overrides
              </button>
              <button
                type="button"
                onClick={onReExtract}
                disabled={isExtracting}
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-slate-300 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-100 disabled:opacity-40"
              >
                {isExtracting ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <RefreshCw size={14} />
                )}
                Re-run extraction
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
